import type { files } from "@dropbox/api-v2-client/types/dropbox_types";
import { Text } from "@dropbox/dig-components/typography";
import {
  useFolderList,
  useFolderMetadata,
} from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/FSS/api";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useConversationEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import {
  ConversationSource,
  type ConversationEvent,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface FSSAgentPayload {
  folder_id: string;
  // TODO: remove the fields below once we do folder ingestion on the server instead
  folder_path: string | undefined;
  num_files: number;
  num_subfolders: number;
  file_list: string;
  subfolder_list: string;
}

export function useFSSOpsHandling() {
  const isEnabled = useFSSOpsEnabled();
  const [searchParams] = useSearchParams();
  const { conversationEvent, folderID, folderMetadata, folderList } =
    useFSSFolderInfo(isEnabled, searchParams);

  // TODO: header is only used in the PoC, this should be removed once we have proper design support
  let header: React.ReactNode | null = null;
  if (isEnabled) {
    header =
      folderID && folderMetadata && folderList ? (
        <FSSHeader folderMetadata={folderMetadata} folderList={folderList} />
      ) : conversationEvent === undefined ||
        conversationEvent.source === "fss-folder" ? (
        <div>FSS Ops Loading...</div>
      ) : null;
  }

  const agentPayload = useAgentPayload(folderID, folderMetadata, folderList);
  return {
    header,
    agentPayload,
  };
}

export function useFSSOpsEnabled() {
  const featureFlagEnabled = useFeatureFlagValueBool(
    "dash_assist_2025_07_15_fss_ops_agent",
  );
  if (!featureFlagEnabled) {
    return false;
  }
  if (!isEmbedPage()) {
    return false;
  }
  return true;
}

interface FSSHeaderProps {
  folderMetadata: files.FolderMetadata;
  folderList: files.ListFolderResult["entries"];
}
export const FSSHeader = memo(
  ({ folderMetadata, folderList }: FSSHeaderProps) => {
    if (!folderMetadata) {
      // still loading / disabled
      return null;
    }
    return (
      <div>
        <Text color="faint">
          FSS Ops Ready: <b>{folderMetadata?.name}</b> (
          {`${folderList?.length} items`})
        </Text>
      </div>
    );
  },
);
FSSHeader.displayName = "FSSHeader";

// load folder metadata + list from the FSS API for the current folder when enabled
// otherwise, return undefined for all values
function useFSSFolderInfo(isEnabled: boolean, searchParams: URLSearchParams) {
  const [folderID, setFolderID] = useState<string | undefined>(undefined);
  const conversationEvent = useConversationEvents(ConversationSource.FssFolder);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const folderID = getFolderID(searchParams, conversationEvent);
    if (folderID) {
      setFolderID(folderID); // keep the last defined folder ID
    }
  }, [isEnabled, searchParams, conversationEvent]);

  const folderMetadata = useFolderMetadata(folderID);
  const folderList = useFolderList(folderID);
  return {
    conversationEvent,
    folderID,
    folderMetadata,
    folderList,
  };
}

export function getFolderID(
  searchParams: URLSearchParams,
  conversationEvent: ConversationEvent | undefined,
) {
  const testFolderID = searchParams.get("test_folder_id");
  if (testFolderID) {
    return testFolderID;
  }
  if (conversationEvent) {
    return parseFolderID(conversationEvent);
  }
  return undefined;
}

function parseFolderID(
  conversationEvent: ConversationEvent,
): string | undefined {
  if (conversationEvent.source !== "fss-folder") {
    return undefined;
  }
  return conversationEvent.data.folders[0].fileId;
}

function getFileMetadataString(file: files.FileMetadata) {
  return `* ${file.name} (Last modified: ${file.client_modified})`;
}

function getSubfolderMetadataString(subfolder: files.FolderMetadata) {
  return `${subfolder.name} ${subfolder.client_modified !== undefined ? `(Last modified: ${subfolder.client_modified})` : ""}`;
}

function useAgentPayload(
  folderID: string | undefined,
  folderMetadata: files.FolderMetadata | undefined,
  folderList: files.ListFolderResult["entries"] | undefined,
): FSSAgentPayload | undefined {
  return useMemo(() => {
    if (!folderID || !folderMetadata || !folderList) {
      return undefined;
    }
    return getAgentPayload(folderID, folderMetadata, folderList);
  }, [folderMetadata, folderList, folderID]);
}

// payload matching the FSS Ops agent's payload format:
// https://github.com/dropbox-internal/server/blob/99b0f98212b3309c42464acd2b7498aa7016ed07/dropbox/context_engine/conversations/internal/handlers/fss_ops/fss_ops_handler.py#L161
function getAgentPayload(
  folderID: string,
  folderMetadata: files.FolderMetadata,
  folderList: files.ListFolderResult["entries"],
): FSSAgentPayload {
  const files = folderList.filter((entry) => entry[".tag"] === "file");
  const subfolders = folderList.filter((entry) => entry[".tag"] === "folder");
  const fileList = files.map(getFileMetadataString).join("\n");
  const subfolderList = subfolders.map(getSubfolderMetadataString).join("\n");
  return {
    folder_id: folderID,
    folder_path: folderMetadata.path_lower,
    num_files: files.length,
    num_subfolders: subfolders.length,
    file_list: fileList,
    subfolder_list: subfolderList,
  };
}
